import React, { useEffect, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { AddRounded as AddIcon, AccessTime as ClockIcon } from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, ChoiceChipGroup, TrTimePicker } from "../../../atoms";
import DrawerField from "../../../molecules/DrawerField";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import useTerms from "../../../../hooks/useTerms";

// Sık kullanılan doz saatleri (sabah · öğle · akşam · yatmadan önce)
const PRESETS = ["07:00", "08:00", "09:00", "12:00", "13:00", "18:00", "19:00", "21:00", "22:00"];

/**
 * İlaç alarm planına doz saati eklemek için sağdan açılan drawer: hazır
 * saatlerden çoklu seçim + listede olmayan saat için 15 dk aralıklı seçici.
 * Planda zaten olan saatler "Ekli" (pasif) görünür.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string[]} existing - Planda olan saatler ("HH:mm")
 * @param {(times: string[]) => void} onSubmit - Seçilen yeni saatler
 */
export default function DoseTimeDrawer({ open, onClose, existing = [], onSubmit }) {
  const terms = useTerms();
  const [picked, setPicked] = useState([]);
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (open) {
      setPicked([]);
      setCustom("");
    }
  }, [open]);

  const options = PRESETS.map((t) => ({
    value: t,
    label: existing.includes(t) ? `${t} · Ekli` : t,
    disabled: existing.includes(t),
  }));

  const customIsNew = custom && !existing.includes(custom) && !picked.includes(custom);
  const all = [...picked, ...(customIsNew ? [custom] : [])];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<ClockIcon />}
        title="Doz Saati Ekle"
        subtitle={`${terms.patientGen} telefonu bu saatlerde alarm çalar`}
        onClose={onClose}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5, display: "grid", rowGap: 2.5 }}>
        <DrawerField label="Hazır saatler" hint="Birden fazla saat seçebilirsiniz.">
          <ChoiceChipGroup
            multiple
            options={options}
            value={picked}
            onChange={setPicked}
            ariaLabel="Hazır saatler"
            sx={{ mt: 0.75, fontVariantNumeric: "tabular-nums" }}
          />
        </DrawerField>

        <DrawerField label="Başka bir saat">
          <Box sx={{ mt: 0.5 }}>
            <TrTimePicker value={custom} onChange={setCustom} startTime="00:00" endTime="23:45" label="Saat seçin" />
          </Box>
          {custom && existing.includes(custom) ? (
            <Typography sx={{ mt: 0.5, fontSize: TYPO.caption, color: COLORS.warningDark }}>
              {custom} planda zaten var.
            </Typography>
          ) : null}
        </DrawerField>
      </Box>

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={() => onSubmit([...new Set(all)].sort())}
        submitLabel={all.length ? `Ekle (${all.length})` : "Ekle"}
        disabled={all.length === 0}
        startIcon={<AddIcon />}
        fullWidth
      />
    </Drawer>
  );
}
